// src/components/Navigation.jsx - Complete version without course dropdown
import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import {
  LogOut,
  Menu,
  X,
  Search,
  ChevronRight,
  Sparkles,
  Settings as SettingsIcon,
  HelpCircle,
  Receipt,
  Bell,
  CheckCheck,
  Home as HomeIcon,
  BookOpen,
  Film,
  PanelLeft,
} from "lucide-react";
import { PAGES, routeForPage, isModifiedClick } from "../utils/constants";
import {
  preloadVisibleThumbnails,
} from "../utils/thumbnailPreloader";
import { getWizardAvatar } from "../assets/avatars";
import { useAuth } from "../AuthContext";
import { useLoginGate } from "./LoginGateProvider";
import { useNotifications } from "../hooks/useNotifications";
import { useT } from '../i18n/useT';
import { Clock } from "lucide-react";


const Navigation = ({
  currentPage,
  setCurrentPage,

  user,
  credits,
  creditsLoading,
  isOutOfCredits,
  dailyUsage,
  onSignOut,
  preloadedData,
  searchQuery: externalSearchQuery,
  setSearchQuery: onSearch,
}) => {
  const [isNavigating, setIsNavigating] = useState(false);
  const [isPreloading, setIsPreloading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const navigationQueueRef = useRef(null);
  const preloadPromiseRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownClickedRef = useRef(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);
  /* THE FIELD MIRRORS THE URL, because the URL is what is actually filtering.
   *
   * This was `useState("")` and never read the location, so any page reached with `?q=` —
   * a shared link, browser Back, or returning from a video opened out of search results —
   * showed FILTERED results above an EMPTY search box. You saw a fraction of your library
   * with nothing on screen explaining why, and no obvious way to undo it. An invisible
   * filter is worse than either answer to "should the search persist".
   *
   * Syncing on the URL's query rather than on every render means typing is never clobbered:
   * the field only submits on Enter, so the URL cannot change mid-word. Navigating to a page
   * without `?q=` clears it, which is correct — the nav's Workspace link is plain
   * `/workspace`, so clicking the tab is a request for the Workspace, not for your last
   * search. Back and drill-down-and-return keep the query because they keep the URL.
   */
  const location = useLocation();
  const urlQuery = new URLSearchParams(location.search).get("q") || "";
  const [navSearchInput, setNavSearchInput] = useState(urlQuery);
  useEffect(() => { setNavSearchInput(urlQuery); }, [urlQuery]);
  const [searchFocused, setSearchFocused] = useState(false);
  // Sidebar width lives in a CSS custom property rather than context: <main> and the
  // workspace composer both need to offset by it, and they are nowhere near this component
  // in the tree. Written on :root so any fixed element can read it.
  // Starts COLLAPSED. Only a stored "0" opens it, so a first visit gets the rail and a
  // returning user still gets whatever they last chose — the toggle writes both values, so
  // an explicit expand survives, while a null (never touched it) reads as collapsed.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("magi_sidebar_collapsed") !== "0"; } catch { return true; }
  });
  useEffect(() => {
    // 64 matches the reference rail (measured ~53-66 CSS). 200 open: the longest label
    // ("Workspace") plus icon and padding needs ~160, and 248 left a dead strip of white
    // to the right of every item.
    // 56px collapsed, matching Kick's rail. 64 read as a panel rather than a strip of
    // icons; the icons themselves are 16-20px, so the extra 8px was pure padding.
    const w = collapsed ? "56px" : "200px";
    // SAME PAIR OF CONDITIONS AS `md`, because this variable's whole job is to report the
    // width of a rail that `md:` is what renders. A rotated phone is 852px wide and so a
    // width-only query called it desktop and published a 56px rail that was not on screen —
    // the Workspace composer reads this as its `left`, so it sat indented from the edge with
    // nothing in the gap, and --magi-nav-w went to 56px where the header is full width.
    const desktop = window.matchMedia("(min-width: 768px) and (min-height: 500px)");
    const apply = () => {
      document.documentElement.style.setProperty("--magi-sidebar", desktop.matches ? w : "0px");
      document.documentElement.style.setProperty("--magi-nav-w", desktop.matches ? w : "100%");
    };
    apply();
    desktop.addEventListener("change", apply);
    try { localStorage.setItem("magi_sidebar_collapsed", collapsed ? "1" : "0"); } catch {}
    return () => desktop.removeEventListener("change", apply);
  }, [collapsed]);
  const searchInputRef = useRef(null);

  const { userData, authLoading } = useAuth();
  const t = useT();
  const { openLoginGate } = useLoginGate();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications(user?.uid);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef(null);
  // Phone navigation drawer. Replaces the bottom tab bar — see the drawer markup below.
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Phone search: an icon until it is asked for. See the mobile slot below.
  const [searchOpen, setSearchOpen] = useState(false);

  // Escape closes it, and the page behind must not scroll under the panel.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setDrawerOpen(false); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [drawerOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showDropdown && !activeDropdown && !showNotifications) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeDropdown();
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      // Close navigation dropdown when clicking outside
      if (activeDropdown && !event.target.closest(".relative")) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDropdown, activeDropdown, showNotifications]);

  // Cleanup preload timeout on unmount
  useEffect(() => {
    return () => {
      if (preloadTimeoutRef.current) {
        clearTimeout(preloadTimeoutRef.current);
      }
    };
  }, []);

  // One search field, two homes: inline on mobile, pinned top-right on desktop. Rendered
  // twice with distinct ids so the two copies never collide in the DOM.
  const onExplore = currentPage === PAGES.EXPLORE;
  // The field is only meaningful where there is a corpus to search: Explore (the whole
  // catalogue) and the Workspace (your own videos). On Home it searched a page you were not
  // looking at, and elsewhere it searched nothing at all.
  const showSearchField =
    currentPage === PAGES.EXPLORE ||
    currentPage === PAGES.WORKSPACE;
  // NOT on "video-player", and no longer on "study-session" either. The bar is off there for
  // the same reason: it floated over the top of the frame, and the 80px band the page had to
  // reserve to clear it pushed the video down the screen for a control nobody uses while
  // watching. Homework, lecture and self-study sessions are watch surfaces — they should read
  // as the video page, which is the whole point of them sharing its geometry.
  // A page can host the search field inline by rendering an empty
  // <div id={`magi-search-slot-${page}`} />; the field is portalled into it and the fixed
  // overlay is skipped. Without this the bar is `fixed top-3` and floats over whatever the
  // page put at the top — on Explore it had to be shoved into the right corner purely to
  // avoid landing on the category tabs.
  //
  // The id is per-PAGE, not a single shared one, because Explore and the Workspace are both
  // always-mounted (hidden, not unmounted). One shared id would put two elements with the
  // same id in the DOM at once and getElementById would return whichever mounted first.
  const [searchSlot, setSearchSlot] = useState(null);
  useEffect(() => {
    if (!showSearchField) { setSearchSlot(null); return; }
    // After paint: the page owning the slot renders in the same commit as this effect.
    const raf = requestAnimationFrame(() =>
      setSearchSlot(document.getElementById(`magi-search-slot-${currentPage}`)));
    return () => cancelAnimationFrame(raf);
  }, [currentPage, showSearchField]);

  const renderSearch = (variant) => (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (navSearchInput.trim() && onSearch) {
          onSearch(navSearchInput.trim());
        }
      }}
      /* max-w-lg caps the pill at 512px, which is right for the corner and centred variants.
         The session page positions the wrapper to span the player exactly, so the cap there
         left the wrapper 936px wide and the visible box stopping at 512. */
      className={`flex w-full relative ${
        (currentPage === "study-session" || currentPage === "video-player") ? "" : "max-w-lg"
      }`}
    >
      <label htmlFor={`nav-search-${variant}`} className="sr-only">Search videos</label>
      <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] pointer-events-none ${
        onExplore ? "text-[#635BFF]" : "text-slate-400"
      }`} />
      <input
        id={`nav-search-${variant}`}
        ref={variant === "mobile" ? searchInputRef : undefined}
        type="text"
        value={navSearchInput}
        onChange={(e) => setNavSearchInput(e.target.value)}
        placeholder={onExplore ? t('nav_search') : t('nav_search_yours')}
        // The glow is an inline style, not an arbitrary Tailwind class: the commas inside
        // rgba() break Tailwind's arbitrary-value parser, which silently emitted a shadow
        // with transparent colours.
        /* The resting glow is gone. That ring-plus-20px-blur existed to lift a pill that
           FLOATED over the page; now that the field sits inside Explore's tab row it made a
           flat toolbar look like it had something hovering in it. A focus ring only — the
           border below carries the resting state. */
        style={onExplore ? {
          boxShadow: searchFocused ? "0 0 0 4px rgba(99,91,255,0.14)" : "none",
        } : undefined}
        onFocus={() => setSearchFocused(true)}
        onBlur={() => {
          setSearchFocused(false);
          // Only collapse an EMPTY field: putting away a query someone typed, because they
          // tapped elsewhere for a moment, would lose their work.
          if (variant === "mobile" && !navSearchInput.trim()) setSearchOpen(false);
        }}
        className={`w-full h-11 pl-11 pr-4 rounded-full text-[15px] text-slate-900 placeholder-slate-400 focus:outline-none transition-all border ${
          onExplore
            ? "bg-white border-[#635BFF]/40 focus:border-[#635BFF]"
            : "bg-slate-100 border-slate-200 focus:bg-white focus:ring-2 focus:ring-[#635BFF]/30 focus:border-[#635BFF]"
        }`}
      />
    </form>
  );

  const navItems = [
    { id: PAGES.HOME, label: t('nav_home'), icon: HomeIcon },
    { id: PAGES.WORKSPACE, label: t('nav_workspace'), icon: BookOpen },
    { id: PAGES.EXPLORE, label: t('nav_explore'), icon: Film },
  ];

  const closeDropdown = () => {
    clearTimeout(dropdownCloseTimer.current);
    setShowDropdown(false);
    dropdownClickedRef.current = false;
  };

  /* THE MENU DOES NOT CLOSE THE INSTANT THE CURSOR LEAVES. The avatar is a 32px target at the
     bottom of a narrow rail and the panel opens up and to the RIGHT of it, so the natural
     move — diagonally towards a menu item — leaves the avatar's box before it reaches the
     panel's, passing through a sliver that belongs to neither. Closing on that sliver made
     the menu impossible to reach unless you traced an L around the corner. A short grace
     period covers the crossing; re-entering either element inside it cancels the close, so
     nothing lingers once you have actually moved away. */
  const dropdownCloseTimer = useRef(null);
  const openDropdownOnHover = () => {
    clearTimeout(dropdownCloseTimer.current);
    if (!dropdownClickedRef.current) setShowDropdown(true);
  };
  const closeDropdownOnHover = () => {
    clearTimeout(dropdownCloseTimer.current);
    if (dropdownClickedRef.current) return;   // pinned open by a click — hover cannot close it
    dropdownCloseTimer.current = setTimeout(() => setShowDropdown(false), 320);
  };
  useEffect(() => () => clearTimeout(dropdownCloseTimer.current), []);

  const handleUserMenuClick = (action) => {
    closeDropdown();
    if (action === "signout") {
      onSignOut();
    }
  };

  const isSubscriber =
    credits?.subscription_plan && credits.subscription_plan !== "free";

  const getVideoUsageText = () => {
    if (isSubscriber) {
      const used = credits.used_this_month || 0;
      return t('nav_used_this_month', { used, cap: credits.monthly_cap });
    }
    // Free logged-in user — show daily usage
    if (dailyUsage) {
      // nav_used_this_month, not nav_used_today: the signed-in free bucket is per calendar
      // month now. This key already existed with the correct sentence, already translated
      // into all 15 locales — reusing it avoids editing an English string the sync would
      // then have to re-translate.
      return t('nav_used_this_month', { used: dailyUsage.used, cap: dailyUsage.daily_limit });
    }
    return t('nav_free_daily');
  };

  // Preload thumbnails on hover with debouncing
  const preloadTimeoutRef = useRef(null);

  // Handle navigation click with proper preloading and loading animation
  // Destinations that are somebody's account, not a place. Clicking one signed out ASKS IN
  // PLACE rather than navigating: routing there and covering it with a full-screen gate is
  // what we just deleted, and bouncing to the landing page throws away the tab they clicked.
  // The prompt names what the account gets them, and the page they were on stays put.
  const ACCOUNT_ONLY = {
    // Home is the composer — the place you make a video — so it asks the same thing making a
    // video asks. It is NOT a way back to the marketing page; the logo is (see below), which
    // is why the two do not share this path.
    [PAGES.HOME]: ["auth_generate_title", "auth_generate_desc"],
    [PAGES.EXPLORE]: ["auth_browse_title", "auth_browse_desc"],
    [PAGES.WORKSPACE]: ["auth_workspace_title", "auth_workspace_desc"],
  };
  const isSignedOut = !user || user.isAnonymous;

  const handleNavClick = useCallback(
    async (pageId) => {
      if (isSignedOut && ACCOUNT_ONLY[pageId]) {
        const [title, subtitle] = ACCOUNT_ONLY[pageId];
        openLoginGate({ title: t(title), subtitle: t(subtitle) });
        return;
      }
      console.log("🖱️ Navigation click:", pageId, "currentPage:", currentPage);
      console.log(
        "📍 PAGES.LIBRARY:",
        PAGES.LIBRARY,
        "PAGES.CREATE:",
        PAGES.CREATE,
      );

      // If we're clicking on the current page, no need to navigate
      if (currentPage === pageId) {
        console.log("⏸️ Already on page:", pageId);
        return;
      }

      // Cancel any previous navigation
      if (navigationQueueRef.current && navigationQueueRef.current !== pageId) {
        console.log("⚠️ Cancelling previous navigation");
        navigationQueueRef.current = null;
      }

      navigationQueueRef.current = pageId;

      // Start navigation animation immediately for ALL page transitions
      setIsNavigating(true);
      setLoadingProgress(0); // Start at 0%

      try {
        // Get items to preload based on target page
        let itemsToPreload = [];
        const MAX_ITEMS = 6;

        if (preloadedData) {
          // No thumbnail preloading needed for current nav pages
        }

        let preloadResult = null;

        // Preload thumbnails if there are any
        if (itemsToPreload.length > 0) {
          console.log(
            `🖼️ Preloading ${itemsToPreload.length} thumbnails for ${pageId}...`,
          );
          setIsPreloading(true);

          // Wait for ALL thumbnails to load completely with progress tracking
          try {
            preloadResult = await preloadVisibleThumbnails(itemsToPreload, {
              onProgress: (stats) => {
                const progress = Math.round(stats.progress * 100);
                setLoadingProgress(progress);
                console.log(
                  `📊 Loading: ${progress}% (${stats.loaded}/${stats.total})`,
                );
              },
            });
            console.log(
              `✅ All ${preloadResult.loaded || 0} thumbnails loaded for ${pageId}`,
            );
            setLoadingProgress(100); // Ensure we reach 100%
          } catch (error) {
            console.warn("⚠️ Some thumbnails failed to load:", error);
            preloadResult = { loaded: 0, failed: 0 };
            setLoadingProgress(100); // Still complete on error
          }

          setIsPreloading(false);
        } else {
          // No thumbnails to load, jump to 100%
          setLoadingProgress(100);
        }

        // Only navigate if this is still the requested navigation
        if (navigationQueueRef.current === pageId) {
          console.log("✅ Calling setCurrentPage with:", pageId);
          setCurrentPage(pageId);

          // Keep loading bar visible briefly so completion is visible
          setTimeout(() => {
            setIsNavigating(false);
            setLoadingProgress(0); // Reset for next navigation
          }, 300);
        }
      } catch (error) {
        console.error("Error during navigation:", error);
        // Navigate anyway on error
        if (navigationQueueRef.current === pageId) {
          setCurrentPage(pageId);
        }
        setIsNavigating(false);
      } finally {
        setIsPreloading(false);
        navigationQueueRef.current = null;
        preloadPromiseRef.current = null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentPage, setCurrentPage, preloadedData, isSignedOut, openLoginGate, t],
  );

  const handleNavHover = useCallback(
    (pageId) => {
      // Clear any existing timeout
      if (preloadTimeoutRef.current) {
        clearTimeout(preloadTimeoutRef.current);
      }

      // No hover preloading needed for current nav pages
    },
    [preloadedData],
  );

  const getTimeAgo = (dateStr) => {
    const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (seconds < 60) return t('nav_just_now');
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return t('nav_minutes_ago', { n: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return t('nav_hours_ago', { n: hours });
    const days = Math.floor(hours / 24);
    if (days < 7) return t('nav_days_ago', { n: days });
    return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  return (
    <>
      <nav
        aria-label="Main navigation"
        // One element, two shapes: a top bar below md (where the bottom tab bar carries
        // navigation), a fixed left rail from md up. Reflowing the same markup keeps the
        // search, notification and profile menus in one place instead of duplicating them.
        className="bg-white border-slate-200 fixed z-50 inset-x-0 top-0 h-16 border-b md:inset-y-0 md:right-auto md:h-auto md:border-b-0 md:border-r"
        style={{ width: "var(--magi-nav-w)" }}
        data-collapsed={collapsed ? "1" : "0"}
        /* Opts this bar into the post-navigation re-anchor. iOS strands fixed elements when
           the URL bar expands under them — see utils/reanchorFixed. */
        data-fixed-chrome=""
      >
        {/* md:px-2 rather than px-3: at 56px the rail has 40px of usable width, which is
            what the icon buttons need. */}
        <div className="px-6 sm:px-8 md:px-2 md:h-full">
          <div className="flex items-center h-16 md:flex-col md:items-stretch md:h-full md:py-4 md:gap-1">
            {/* Left side - Logo and Navigation */}
            <div className={`flex items-center flex-shrink-0 md:px-2 md:mb-4 ${
              collapsed ? "md:justify-center" : "md:justify-between"
            }`}>
              {/* Logo — the wordmark cannot fit a 76px rail, so collapsed drops it and
                  leaves the toggle alone in the header slot. */}
              {/* Three lines, top left — the phone's way into the app now that the bottom
                  tab bar is gone. Hidden from md up, where the left rail is always on screen
                  and a menu button would be a second door to the same room. */}
              <button
                onClick={() => setDrawerOpen(true)}
                aria-label="Open menu"
                aria-expanded={drawerOpen}
                className="md:hidden flex-shrink-0 -ml-1 mr-1 p-2 text-slate-700 hover:text-slate-900 transition-colors"
              >
                <Menu className="w-7 h-7" />
              </button>
              <a
                href={routeForPage(PAGES.HOME)}
                onClick={(e) => {
                  if (isModifiedClick(e)) return;
                  e.preventDefault();
                  // The LOGO is the front door and always opens: for a signed-out visitor "/"
                  // is the landing page, which is exactly where a stranger clicking the
                  // wordmark should land. It deliberately skips handleNavClick's account-only
                  // check — prompting here would leave someone with no way out of the app.
                  setCurrentPage(PAGES.HOME);
                }}
                aria-label="Magi home"
                className={`flex-shrink-0 flex items-center no-underline ${collapsed ? "md:hidden" : ""}`}
              >
                <span className="text-[36px] font-bold text-[#635BFF] tracking-tight" style={{ fontFamily: "'Satoshi', sans-serif" }}>
                  Magi
                </span>
              </a>

              <button
                onClick={() => setCollapsed((c) => !c)}
                aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                className="hidden md:flex w-8 h-8 rounded-lg items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              >
                <PanelLeft className="w-4 h-4" />
              </button>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex space-x-1 ml-8 md:flex-col md:space-x-0 md:space-y-1 md:ml-0">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive =
                    currentPage === item.id || currentPage?.startsWith(item.id + "-") ||
                    (item.submenu &&
                      item.submenu.some(
                        (subItem) => currentPage === subItem.id,
                      ));

                  if (item.submenu) {
                    // Dropdown menu item - click to toggle, hover also works on desktop
                    const isDropdownOpen = activeDropdown === item.id;
                    return (
                      <div key={item.id} className="relative">
                        <button
                          onClick={() => setActiveDropdown(isDropdownOpen ? null : item.id)}
                          className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                            isActive
                              ? "bg-[#635BFF]/10 text-[#635BFF]"
                              : "text-slate-800 hover:text-slate-900 hover:bg-slate-100"
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          <span>{item.label}</span>
                          <ChevronRight className={`w-3 h-3 transform transition-transform ${isDropdownOpen ? "rotate-90" : "rotate-90"}`} />
                        </button>

                        {/* Dropdown Menu - click to toggle */}
                        <div className={`absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50 transition-all duration-200 ${isDropdownOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"}`}>
                          {item.submenu.map((subItem) => {
                            const SubIcon = subItem.icon;
                            const isSubActive = currentPage === subItem.id;

                            return (
                              <button
                                key={subItem.id}
                                onClick={() => handleNavClick(subItem.id)}
                                onMouseEnter={() => handleNavHover(subItem.id)}
                                className={`w-full flex items-center space-x-3 px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                                  isSubActive
                                    ? "text-[#635BFF] bg-[#635BFF]/10"
                                    : "text-gray-700"
                                }`}
                              >
                                <SubIcon className="w-4 h-4" />
                                <span>{subItem.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  } else {
                    // Regular menu item. An <a href>, not a <button>: these were buttons
                    // calling a function, so the browser saw no link — cmd-click, middle
                    // click and "Open link in new tab" all did nothing, and a right-click
                    // offered the page menu instead of a link menu. The click handler still
                    // runs the in-app transition, but only for a plain left click; anything
                    // modified is handed back to the browser untouched.
                    return (
                      <a
                        key={item.id}
                        href={routeForPage(item.id) || "#"}
                        onClick={(e) => {
                          if (isModifiedClick(e)) return;
                          e.preventDefault();
                          handleNavClick(item.id);
                        }}
                        onMouseEnter={() => handleNavHover(item.id)}
                        aria-current={isActive ? "page" : undefined}
                        // Collapsed: the icon alone, centred, with the label as a native
                        // tooltip so the rail is still navigable without the words.
                        title={collapsed ? item.label : undefined}
                        className={`flex items-center gap-3 py-2 rounded-md text-[15px] font-medium transition-colors no-underline ${
                          collapsed ? "md:justify-center md:px-0" : "px-3"
                        } ${
                          isActive
                            ? "bg-[#635BFF]/10 text-[#635BFF]"
                            : "text-slate-800 hover:text-slate-900 hover:bg-slate-100"
                        }`}
                      >
                        {Icon && <Icon className="w-[18px] h-[18px] shrink-0" />}
                        <span className={collapsed ? "md:hidden" : ""}>{item.label}</span>
                      </a>
                    );
                  }
                })}
            </div>

            {/* Search — inline in the mobile top bar only. On desktop it is pinned to the
                top right of the content area instead (rendered after this nav): a left rail
                is the wrong place to go hunting for a search field.

                ON A PHONE IT IS AN ICON UNTIL IT IS ASKED FOR. The field is the widest thing
                in a 393px header, and it sat there permanently between the wordmark and the
                account controls for a gesture most visits never make. Tapping the magnifier
                expands it across the row; blurring an empty field puts it away again, so it
                never needs a second control to dismiss it. */}
            <div className="flex md:hidden flex-1 items-center justify-end px-2">
              {showSearchField && (
                searchOpen ? (
                  <div className="flex-1">{renderSearch("mobile")}</div>
                ) : (
                  <button
                    onClick={() => {
                      setSearchOpen(true);
                      // Focus after paint — the input does not exist yet on this tick.
                      requestAnimationFrame(() => searchInputRef.current?.focus());
                    }}
                    aria-label={t('nav_search')}
                    aria-expanded={false}
                    className="p-2 text-slate-900 hover:text-slate-600 transition-colors"
                  >
                    <Search className="w-6 h-6" />
                  </button>
                )
              )}
            </div>

            {/* Right side - Notifications & User Menu */}
            {/* space-y-4 on the rail, not space-y-1: at 4px the bell sat almost touching the
                avatar and the two read as one control. The horizontal space-x-5 is the mobile
                top bar, where they are already far apart — only the vertical rail needed it. */}
            <div className={`flex items-center space-x-5 flex-shrink-0 md:mt-auto md:flex-col md:space-x-0 md:space-y-4 ${
              collapsed ? "md:items-center" : "md:items-stretch"
            }`}>
              {/* Notification Bell - only for logged-in users */}
              {user && !user.isAnonymous && (
                <div className="relative" ref={notificationRef}>
                  <button
                    onClick={() => setShowNotifications(v => {
                      if (!v) markAllAsRead();
                      return !v;
                    })}
                    aria-label="Notifications"
                    className="relative p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-full transition-colors"
                  >
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute top-1 right-1 min-w-[16px] h-4 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>

                  {/* Notification Dropdown */}
                  {showNotifications && (
                    /* POSITIONED FOR BOTH SHAPES OF THIS NAV, like the profile menu below it.
                       `right-0 top-full` alone is a TOP BAR's geometry, and from md up this is
                       a fixed LEFT RAIL with the bell at the bottom of it — so the panel was
                       right-aligned to a button 8px from the left edge of a 56px rail and then
                       drawn 320px wide, putting almost all of it off the left of the screen,
                       and opened DOWNWARD from a control already at the bottom of a full-height
                       rail. Beside the rail and bottom-aligned on desktop; unchanged under md,
                       where the bell really is in a top bar near the right edge.

                       max-w on the mobile side because w-80 is 320px and a narrow phone has
                       less than that between the bell's right edge and the left of the screen. */
                    <div className="absolute right-0 top-full mt-2
                                    md:right-auto md:left-full md:top-auto md:bottom-0 md:mt-0 md:ml-3
                                    w-80 max-w-[calc(100vw-2rem)] max-h-[480px] bg-white rounded-xl
                                    shadow-xl border border-gray-200 overflow-hidden z-50">
                      {/* Header */}
                      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                        <h3 className="font-semibold text-gray-900 text-sm">{t('nav_notifications')}</h3>
                        {unreadCount > 0 && (
                          <button
                            onClick={markAllAsRead}
                            className="flex items-center gap-1 text-xs text-[#635BFF] hover:text-[#5851e0] font-medium"
                          >
                            <CheckCheck className="w-3.5 h-3.5" />
                            {t('nav_mark_all_read')}
                          </button>
                        )}
                      </div>

                      {/* Notification List */}
                      <div className="overflow-y-auto max-h-[400px]">
                        {notifications.length === 0 ? (
                          <div className="px-4 py-12 text-center">
                            <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                            <p className="text-sm text-gray-500">{t('nav_no_notifications')}</p>
                            <p className="text-xs text-gray-400 mt-1">{t('nav_no_notifications_sub')}</p>
                          </div>
                        ) : (
                          notifications.map(notification => {
                            const timeAgo = getTimeAgo(notification.createdAt);
                            return (
                              <div
                                key={notification.id}
                                className="w-full text-left px-4 py-3 flex items-center gap-3 border-b border-gray-50"
                              >
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm leading-snug text-gray-600">
                                    {notification.title}
                                  </p>
                                  <p className="text-xs text-gray-400 mt-1">{timeAgo}</p>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* User Authentication */}
              {user && !user.isAnonymous ? (
                <div
                  className="relative"
                  ref={dropdownRef}
                  onMouseEnter={openDropdownOnHover}
                  onMouseLeave={closeDropdownOnHover}
                >
                  <button
                    onClick={() => {
                      if (dropdownClickedRef.current) {
                        // Already pinned — close it
                        closeDropdown();
                      } else {
                        // Pin it open (whether from hover or closed)
                        setShowDropdown(true);
                        dropdownClickedRef.current = true;
                      }
                    }}
                    aria-label="User menu"
                    aria-expanded={showDropdown}
                    className="flex items-center space-x-2 text-slate-600 hover:text-slate-900 focus:outline-none">
                    <img
                      src={getWizardAvatar(user.uid, userData)}
                      alt="Avatar"
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <span className={`hidden text-sm font-medium text-slate-700 truncate ${collapsed ? "" : "md:block"}`}>
                      {userData?.displayName ||
                        user.email?.split("@")[0] ||
                        "User"}
                    </span>
                  </button>

                  {/* Profile Dropdown */}
                  {showDropdown && (
                    /* The pl-2/pt-2 is a HOVER BRIDGE, not decoration: it is padding on the
                       positioned wrapper, so the gap between the avatar and the visible card
                       is part of the panel's own hit area. Widened to 3 (12px) on the desktop
                       side, where the panel sits beside the rail rather than under it. */
                    <div className="absolute right-0 top-full pt-2 md:right-auto md:left-full md:top-auto md:bottom-0 md:pt-0 md:pl-3 w-[calc(100vw-2rem)] max-w-[240px] z-50">
                      <div className="bg-white rounded-xl shadow-lg border border-gray-200/80 overflow-hidden">
                        {/* Identity */}
                        <div className="px-4 pt-4 pb-3 border-b border-gray-100">
                          <div className="font-semibold text-sm text-gray-900 truncate">
                            {userData?.displayName || "User"}
                          </div>
                          <div className="text-gray-400 text-xs truncate mt-0.5">
                            {user.email}
                          </div>
                        </div>

                        {/* Nav items */}
                        <div className="py-1.5">
                          {[
                            { label: t('nav_upgrade'), action: () => window.dispatchEvent(new Event("magi:open-subscribe")) },
                            { label: t('nav_billing'), action: () => setCurrentPage(PAGES.BILLING) },
                            { label: t('nav_settings'), action: () => setCurrentPage(PAGES.SETTINGS) },
                            { label: t('nav_api'), action: () => setCurrentPage(PAGES.API) },
                            { label: t('nav_contact'), action: () => setCurrentPage("contact") },
                          ].map(item => (
                            <button
                              key={item.label}
                              onClick={() => { closeDropdown(); item.action(); }}
                              className="w-full text-left px-4 py-2 text-[13px] text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                            >
                              {item.label}
                            </button>
                          ))}
                        </div>

                        {/* Sign out */}
                        <div className="border-t border-gray-100 py-1.5">
                          <button
                            onClick={() => handleUserMenuClick("signout")}
                            className="w-full text-left px-4 py-2 text-[13px] text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
                          >
                            {t('nav_sign_out')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* Anonymous visitors are inside the app now, so the nav has to offer them a
                   way in. Opens the gate in place rather than navigating to /auth. */
                <button
                  onClick={() => openLoginGate()}
                  className="px-4 py-2 bg-[#635BFF] text-white rounded-full text-sm font-semibold hover:bg-[#4B44CC] transition-colors"
                >
                  {t('auth_signin_submit')}
                </button>
              )}
            </div>
          </div>

          {/* Mobile hamburger menu removed — bottom tab bar used instead */}
        </div>
      </nav>

      {/* Desktop search — centred at the top, the way YouTube's is, rather than tucked into
          the right corner. Width is capped so it stays a search box rather than a banner, and
          it sits above page content but below open dropdowns. Centred on the VIEWPORT, not on
          the content column: the left nav is only 64px, and offsetting by it left the box
          visibly off-centre against the window it reads as belonging to. */}
      {/* Inline when the page offers a slot, floating otherwise. The slot wins so a page
          can put the field in its own header row instead of having it hover over the top of
          whatever is there. */}
      {showSearchField && searchSlot && createPortal(
        <div className="hidden md:block w-full">{renderSearch("desktop")}</div>,
        searchSlot,
      )}
      {showSearchField && !searchSlot && (
        <div
          className={`hidden md:block fixed top-3 z-40 ${
            // The session variant is positioned to the pixel, so its wrapper carries no
            // horizontal padding — px-4 there inset the pill 16px inside the edges it was
            // supposed to sit on.
            (currentPage === "study-session" || currentPage === "video-player") ? "" : "px-4"
          } ${
            // Explore keeps it in the right corner. That page has a full-width row of category
            // tabs at the top — Popular / Mathematics / Physics / … — and a centred bar lands
            // straight on top of them. The tabs end well before the right edge, so the corner
            // is the one place on this page it does not cover anything.
            onExplore
              ? "right-1 w-[360px]"
              : currentPage === "study-session"
                // Left edge on the video's left edge (368 = nav rail 56 + page padding 16 +
                // list rail 280 + gutter 16). Centred on the CONTENT area, not the window:
                // the nav rail is chrome, not part of the page, so counting it made the bar
                // sit 28px left of where it looks centred. The inset from the rail's right
                // edge is 368-56 = 312, so mirroring 312 on the right puts the midpoint on
                // (56 + width)/2 at any window size.
                ? "left-[368px] right-[312px]"
              : currentPage === "video-player"
                // Centred on the VIDEO COLUMN, derived rather than hard-coded.
                //
                // The old fixed offsets assumed a 56px nav rail. It is not fixed: Navigation
                // writes --magi-sidebar as 56px collapsed, 200px EXPANDED, 0 under 768px, and
                // <main> pads by it — so expanding the rail slid the page 144px right while a
                // pixel-pinned bar stayed put. That is the mis-centring, and no constant can
                // fix it; the offset has to read the same variable the layout does.
                //
                // Centre of the column = viewport/2 + (sidebar - inset)/2, which is `left-1/2`
                // plus a margin. Written as a margin, not calc(...100%...), because a fixed
                // element's 100% excludes the scrollbar while 100vw includes it — a ~15px
                // error, i.e. the same bug again.
                //   below lg: rail is stacked BELOW the video, column = sidebar+16 .. w-16
                //             -> inset cancels, margin is sidebar/2
                //   lg and up: Up Next sits right, column = sidebar+16 .. w-312
                //             -> margin is (sidebar - 296)/2
                ? "left-1/2 -translate-x-1/2 w-full max-w-[600px] " +
                  "ml-[calc(var(--magi-sidebar,0px)/2)] " +
                  "lg:ml-[calc((var(--magi-sidebar,0px)_-_296px)/2)]"
                : "left-1/2 -translate-x-1/2 w-full max-w-[600px]"
          }`}
        >
          {renderSearch("desktop")}
        </div>
      )}

      {/* YouTube-style Loading Bar */}
      {isNavigating && (
        <div className="fixed top-0 left-0 right-0 h-[2px] z-[100]">
          <div
            className="h-full bg-[#635BFF] transition-all duration-200 ease-out"
            style={{
              width: `${loadingProgress}%`,
              boxShadow: "0 0 10px rgba(99, 91, 255, 0.5)",
            }}
          />
        </div>
      )}

      {/* Phone navigation — a slide-over drawer from the LEFT, opened by the three lines in
          the header. This replaces a fixed bottom tab bar.
          The bar cost 57px of every phone screen permanently, on a product whose screens are
          a video and a list of videos, and it is the one piece of furniture that cannot be
          scrolled away. A drawer costs nothing until it is asked for, and it is the same
          shape as the desktop left rail, so the app has one navigation idea rather than two.

          Kept MOUNTED and translated rather than conditionally rendered, so opening and
          closing both animate; a mount cannot transition from a position it never had. */}
      <div
        className={`md:hidden fixed inset-0 z-[60] ${drawerOpen ? "" : "pointer-events-none"}`}
        aria-hidden={!drawerOpen}
      >
        <div
          onClick={() => setDrawerOpen(false)}
          className={`absolute inset-0 bg-black/50 transition-opacity duration-200 ${
            drawerOpen ? "opacity-100" : "opacity-0"
          }`}
        />
        <nav
          aria-label="Mobile navigation"
          /* 240px, matching YouTube's guide panel. 78%/300px was too much of a phone screen
             for three destinations — a drawer is a list you glance at, not a page. */
          className={`absolute left-0 top-0 h-full w-[240px] bg-white shadow-2xl
            transition-transform duration-200 ease-out ${
              drawerOpen ? "translate-x-0" : "-translate-x-full"
            }`}
        >
          <div className="flex items-center gap-1 h-14 px-2">
            <button
              onClick={() => setDrawerOpen(false)}
              aria-label="Close menu"
              className="p-2 text-slate-900 hover:text-slate-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <span
              className="text-[22px] font-bold text-[#635BFF] tracking-tight"
              style={{ fontFamily: "'Satoshi', sans-serif" }}
            >
              Magi
            </span>
          </div>

          {/* Spacing measured off YouTube's guide: the pill group is inset 12px from the panel
              edge, sits ~14px below the header, and the rows STACK with no gap — a guide is
              one list, and gaps between rows make three destinations read as three separate
              cards. Ours had an 8px inset, a 2px gap and almost nothing under the header, so
              the group sat cramped against the top-left corner. */}
          <div className="flex flex-col px-3 pt-[18px]">
            {[
              { id: PAGES.HOME, label: t('nav_home'), Icon: HomeIcon },
              { id: PAGES.WORKSPACE, label: t('nav_workspace'), Icon: BookOpen },
              { id: PAGES.EXPLORE, label: t('nav_explore'), Icon: Film },
            ].map(({ id, label, Icon }) => {
              const isActive = currentPage === id || currentPage?.startsWith(id + "-");
              return (
                <a
                  key={id}
                  href={routeForPage(id) || "#"}
                  onClick={(e) => {
                    if (isModifiedClick(e)) return;
                    e.preventDefault();
                    setDrawerOpen(false);
                    handleNavClick(id);
                  }}
                  aria-current={isActive ? "page" : undefined}
                  /* The active row is a filled pill, not just coloured text: at this size a
                     colour change alone is easy to miss, and the pill is what says "you are
                     here" at a glance.

                     EVERY LABEL IS NEAR-BLACK, including the inactive ones. They were
                     slate-600, which is the colour of secondary text — and next to YouTube's
                     guide, where every destination is #0f0f0f, ours read as a list of things
                     that were switched off. A destination you can go to is not secondary; the
                     pill already carries which one you are on. */
                  className={`flex items-center gap-3.5 h-10 px-3 rounded-lg no-underline transition-colors text-slate-900 ${
                    isActive ? "bg-slate-100" : "hover:bg-slate-50 active:bg-slate-100"
                  }`}
                >
                  <Icon className="w-6 h-6 flex-shrink-0" strokeWidth={isActive ? 2.3 : 1.9} />
                  <span className={`text-[15px] ${isActive ? "font-semibold" : "font-medium"}`}>
                    {label}
                  </span>
                </a>
              );
            })}
          </div>
        </nav>
      </div>
    </>
  );
};

export default Navigation;
